import { useEffect } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAuth, useAppLayout } from '@artprov/design-system'
import { HeaderOs } from './header/HeaderOs.jsx'
import { useContratoStore } from '../store/contrato.store.js'

/**
 * Clave de sessionStorage donde `main.jsx` guarda la ruta a la que el usuario
 * intentaba acceder antes de que el callback OIDC fuese reenviado a `/`.
 */
export const POST_LOGIN_REDIRECT_KEY = 'post-login-redirect'

/**
 * Layout de las rutas protegidas.
 *
 * Además de renderizar el `<Outlet />`, restaura la ruta original cuando el
 * usuario vuelve de Keycloak y muestra el `HeaderOs` común a todas las páginas
 * protegidas (replicando la configuración de `src/AppRouter.jsx`).
 *
 * Contexto: al recargar (F5) una ruta profunda, `check-sso` de keycloak-js
 * (con `checkLoginIframe: false` y sin `silentCheckSsoRedirectUri`) fuerza una
 * redirección de página completa a Keycloak. El callback OIDC vuelve en el
 * *hash* de la URL sobre la ruta profunda. Como el design-system sólo detecta
 * callbacks en el query string, `main.jsx` reenvía el callback a `/` para que
 * `<AuthKeycloak>` lo procese, guardando antes la ruta original en
 * sessionStorage. Una vez autenticado, este layout navega de vuelta a esa ruta.
 *
 * Este componente sólo se monta cuando `<AuthKeycloak>` ya validó la sesión
 * (el `AccessGuard` no renderiza sus children hasta estar autenticado), por lo
 * que la restauración se ejecuta una única vez al montar.
 *
 * @returns {JSX.Element} El contenido de la ruta protegida.
 */
/**
 * Limpia el idNomina persistido en localStorage cuando el usuario vuelve al
 * inicio (`/`). Vive en este layout (y no dentro de Nomina) porque al navegar
 * fuera de /cargar-nomina el componente Nomina se desmonta y su useEffect ya
 * no se ejecuta con la nueva ubicación. Al estar en el layout de rutas
 * protegidas, permanece montado durante toda la navegación interna.
 */
function useClearNominaOnHome() {
  const location = useLocation()

  useEffect(() => {
    if (location.pathname === '/') {
      localStorage.removeItem('idNomina')
    }
  }, [location.pathname])
}

export default function ProtectedLayout() {
  const navigate = useNavigate()
  const { logout } = useAuth()
  const { setShowHeader } = useAppLayout()

  useClearNominaOnHome()

  // Oculta el Header propio del design-system (`AppRouter`) mientras este
  // layout está montado, para evitar que se rendericen dos headers. Al
  // desmontarse (por ejemplo, al navegar a una ruta pública) se restaura.
  useEffect(() => {
    setShowHeader(false)
    return () => setShowHeader(true)
  }, [setShowHeader])

  const opcionesDelMenu = [
    // { icon: 'person', opcion: 'Mi información' },
    {
      icon: 'power_settings_new',
      opcion: 'Cerrar sesión',
      onClick: async () => {
        // Limpia el contrato/usuario cacheado en localStorage ANTES de cerrar
        // sesión. De lo contrario, si otro usuario inicia sesión en el mismo
        // navegador, `configContratoUser` (inicio.page.jsx) encuentra un
        // `contratoId` persistido y nunca llama a `/v2/usuario/me`, quedando
        // con los datos del usuario anterior.
        useContratoStore.getState().reset()
        await logout()
      },
    },
  ]

  useEffect(() => {
    let target = null
    try {
      target = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY)
    } catch {
      // sessionStorage puede no estar disponible (modo privado); se ignora.
      target = null
    }

    if (!target) return

    try {
      sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY)
    } catch {
      // noop
    }

    const current = `${window.location.pathname}${window.location.search}`
    if (target !== current) {
      // Se usa push (no replace) a propósito: así la entrada de `/` (pantalla
      // inicial) queda en el historial y el botón "Volver" (`navigate(-1)`)
      // regresa a ella en lugar de saltar a la URL de Keycloak.
      navigate(target)
    }
  }, [navigate])

  return (
    <>
      <HeaderOs
        showNotifications={false}
        alertActive={false}
        opcionesDelMenu={opcionesDelMenu}
        className="shadow-md"
        showHelpDesk={true}
        onHelpClick={() => navigate('/help-desk')}
      />
      <Outlet />
    </>
  )
}
